import * as React from 'react'
import { styled } from 'next-yak'

import * as plume from '@ulule/owl-kit-components/next'

import { DisplayMode } from '../../../../context'
import { SIDEBAR_MAX_WIDTH } from '../../style'
import { EDIT_MODE_SMALL_LAPTOP } from '../../constants'

export function NewsSkeleton() {
  const isEditMode = React.useContext(DisplayMode) === 'edit'

  return (
    <Page $isEditMode={isEditMode}>
      <Content>
        {isEditMode && (
          <plume.styles.skeleton.Placeholder height={80}>
            <PlaceholderContent>
              <plume.styles.skeleton.Circle radius={33} />
              <plume.styles.skeleton.Text size="medium" />
            </PlaceholderContent>

            <Help size="small" />
          </plume.styles.skeleton.Placeholder>
        )}

        <Card isEditMode={isEditMode} />
        <Card isEditMode={isEditMode} />
        <Card isEditMode={isEditMode} />
      </Content>

      <Aside $isEditMode={isEditMode}>
        <plume.styles.skeleton.Placeholder height={150}>
          <plume.styles.skeleton.Text size="medium" width={60} />
          <plume.styles.skeleton.Text size="medium" width={40} />
          <plume.styles.skeleton.Rectangle height={40} width={80} />
        </plume.styles.skeleton.Placeholder>

        <RelativeBox>
          <plume.styles.skeleton.Circle radius={56} />

          <plume.styles.skeleton.Rectangle height={250} />
        </RelativeBox>
      </Aside>
    </Page>
  )
}

function Card({ isEditMode }: { isEditMode: boolean }) {
  return (
    <CardWrapper $isEditMode={isEditMode}>
      <plume.styles.skeleton.Rectangle />

      <plume.styles.skeleton.Placeholder height={216}>
        <plume.styles.skeleton.Text size="medium" width={60} />
        <plume.styles.skeleton.Text size="large" width={50} />

        <div>
          <plume.styles.skeleton.Text size="small" width={98} />
          <plume.styles.skeleton.Text size="small" width={65} />
        </div>

        <BottomInformation>
          <Comment>
            <plume.glyphs.stroke.Bubble size={11} />
            <plume.styles.skeleton.Text size="small" width={100} />
          </Comment>

          <See>
            <plume.styles.skeleton.Text size="small" width={100} />
            <plume.glyphs.stroke.ArrowRight size={16} />
          </See>
        </BottomInformation>
      </plume.styles.skeleton.Placeholder>
    </CardWrapper>
  )
}

const BottomInformation = styled.div`
  display: flex;
  gap: 8px;
  justify-content: space-between;
`

const Comment = styled.div`
  display: flex;
  gap: 4px;
  width: 127px;
  align-items: center;

  ${plume.glyphs.stroke.Bubble} {
    color: ${plume.COLORS.PRIMARY_SAND_200};
  }
`

const See = styled.div`
  display: flex;
  gap: 4px;
  width: 155px;
  align-items: center;

  ${plume.glyphs.stroke.ArrowRight} {
    color: ${plume.COLORS.PRIMARY_SAND_200};
  }
`

const Page = styled.div<{ $isEditMode: boolean }>`
  padding: 0 10px;

  @media screen and ${({ $isEditMode }) => ($isEditMode ? EDIT_MODE_SMALL_LAPTOP : plume.BREAKPOINTS.LAPTOP)} {
    box-sizing: border-box;
    display: flex;
    margin: 60px auto;
    max-width: 1200px;
  }
`

const RelativeBox = styled.div`
  position: relative;
  margin-bottom: 30px;

  ${plume.styles.skeleton.Circle} {
    position: absolute;
    top: -28px;
    left: 50%;
    transform: translateX(-50%);
  }
`

const Aside = styled.div<{ $isEditMode: boolean }>`
  @media screen and ${({ $isEditMode }) => ($isEditMode ? EDIT_MODE_SMALL_LAPTOP : plume.BREAKPOINTS.LAPTOP)} {
    margin-left: 45px;
    margin-top: 0;
    max-width: ${({ $isEditMode }) => ($isEditMode ? '336px' : SIDEBAR_MAX_WIDTH)};
    min-width: 336px;
    width: ${({ $isEditMode }) => ($isEditMode ? 'fit-content' : SIDEBAR_MAX_WIDTH)};
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    flex: none;
    max-width: ${SIDEBAR_MAX_WIDTH};
  }

  ${plume.styles.skeleton.Placeholder} {
    align-items: center;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    margin-bottom: 48px;
    padding: 24px 21px;

    ${plume.styles.skeleton.Rectangle} {
      margin-top: 14px;
    }
  }
`

const CardWrapper = styled.div<{ $isEditMode: boolean }>`
  height: 409px;
  position: relative;
  margin-bottom: 30px;

  ${plume.styles.skeleton.Rectangle} {
    height: 199px !important;
  }

  ${plume.styles.skeleton.Placeholder} {
    justify-content: space-between;
    flex-direction: column;
    display: flex;
    background: ${plume.COLORS.PRIMARY_GREY_000};
  }

  &::before {
    background: ${plume.COLORS.PRIMARY_GREY_000};
    border: 1px dashed ${plume.COLORS.PRIMARY_GREY_500};
    bottom: -40px;
    content: '';
    left: 50%;
    position: absolute;
    top: 0px;
    z-index: -1;
  }

  @media screen and ${({ $isEditMode }) => ($isEditMode ? EDIT_MODE_SMALL_LAPTOP : plume.BREAKPOINTS.LAPTOP)} {
    height: 632px;

    ${plume.styles.skeleton.Rectangle} {
      height: 416px !important;
    }
  }
`

const Content = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  max-width: 760px;

  ${plume.styles.skeleton.Placeholder} {
    box-sizing: border-box;
    margin-bottom: 45px;
    padding: 21px 18px;
    text-align: center;
  }

  ${CardWrapper}:last-child {
    &::before {
      content: none;
    }
  }
`

const PlaceholderContent = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
`

const Help = styled(plume.styles.skeleton.Text)`
  box-sizing: border-box;
  margin: 0 auto;
  max-width: 250px;
`
